import { Reveal, Kicker, ArrowCta } from "@/components/Reveal";
import { scrollToSection } from "@/lib/scroll";

const MEANINGS = [
  {
    n: "01",
    title: "IL MIO PUNTO DI PARTENZA",
    text: "Ho girato il mondo, raccolto esperienze e studiato realtà differenti. Tornare in Sicilia significa riportare tutto ciò che ho imparato nel luogo da cui sono partito, con l'obiettivo di contribuire alla crescita delle imprese locali.",
  },
  {
    n: "02",
    title: "IL TUO NUOVO PUNTO DI PARTENZA",
    text: "Starting Point Agency è anche il punto da cui un'azienda può ripartire. Non significa cancellare il proprio passato, ma superare ciò che non funziona più e costruire una nuova direzione con strumenti, idee e strategie aggiornate. Non siamo legati a formule obsolete: studiamo continuamente il mercato e adattiamo il nostro approccio all'evoluzione delle piattaforme, della comunicazione e delle persone.",
  },
];

const EXPERIENCE = [
  { value: "DAL 2017", label: "ESPERIENZA FREELANCE" },
  { value: "30+ CLIENTI", label: "SOCIAL & ADVERTISING — ADDIVENIRE" },
  { value: "GOOGLE", label: "CERTIFICAZIONE DIGITAL MARKETING" },
  { value: "300+ ORE", label: "FORMAZIONE VIDEO PROFESSIONALE" },
];

const SKILLS = ["Social media", "Advertising", "Influencer marketing", "Copywriting", "Editing video", "Branding", "Siti web", "SEO", "Comunicazione online e offline"];

export const Chi = () => (
  <section id="chi" data-testid="chi-section" className="bg-brand-light py-24 lg:py-36">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <Kicker>Chi</Kicker>
        <h2 className="mt-5 max-w-4xl font-display text-4xl font-black leading-[1.02] tracking-tight text-navy sm:text-6xl">
          SONO TORNATO AL MIO PUNTO DI PARTENZA. <span className="text-brand-gradient">PER FAR RIPARTIRE QUALCOSA DI NUOVO.</span>
        </h2>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-12 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Reveal delay={0.1}>
            <figure className="group relative overflow-hidden rounded-2xl shadow-[0_30px_70px_-35px_rgba(10,18,41,0.5)]">
              <span className="absolute inset-x-0 top-0 z-10 h-[4px] bg-brand-gradient" aria-hidden="true" />
              <img
                src="/images/davide-chiavetta.jpg"
                alt="Davide Chiavetta, founder di Starting Point Agency, davanti al Centre Pompidou di Parigi"
                className="aspect-[3/4] w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                loading="lazy"
                data-testid="founder-photo"
              />
              <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy/90 to-transparent p-5 pt-14">
                <p className="font-display text-sm font-extrabold tracking-tight text-white">DAVIDE CHIAVETTA</p>
                <p className="font-mono text-[10px] tracking-[0.25em] text-white/60">FOUNDER — STARTING POINT AGENCY</p>
              </figcaption>
            </figure>
          </Reveal>
        </div>

        <div className="lg:col-span-8">
          <Reveal delay={0.15}>
            <p className="text-lg font-semibold leading-relaxed text-navy">
              Mi chiamo Davide Chiavetta e mi occupo di marketing, comunicazione digitale e crescita dei brand.
            </p>
            <div className="mt-5 space-y-4 text-base leading-relaxed text-slate-600">
              <p>
                Il mio percorso mi ha portato a conoscere realtà, mercati e attività in Europa, America, Asia e Africa. Viaggiare, lavorare e confrontarmi con esperienze diverse mi ha permesso di osservare approcci differenti alla comunicazione e sviluppare una visione trasversale del marketing.
              </p>
              <p>
                Negli anni ho lavorato nella gestione dei social media, nella creazione di contenuti, nell'advertising, nell'influencer marketing, nel branding e nello sviluppo di siti web: come freelance dal 2017, con Two2Bit Digital Agency, e nella gestione di progetti social e advertising per numerose attività.
              </p>
              <p>
                Ho approfondito il digital marketing con la certificazione Google in Digital Marketing, oltre 300 ore di videocorsi professionali e studio continuo delle piattaforme e dei comportamenti del pubblico. Le esperienze commerciali mi hanno insegnato quanto contino l'ascolto delle persone e la concretezza dei risultati.
              </p>
              <p>
                Dopo aver viaggiato e raccolto esperienze, sono tornato in Sicilia, nel luogo in cui sono nato. Ed è qui che ho scelto di costruire il mio prossimo punto di partenza: non volevo portare le mie competenze altrove, volevo metterle a disposizione di un luogo che conosco, in cui credo e che può ancora esprimere tantissimo.
              </p>
              <p className="font-semibold text-navy">Da questa idea nasce Starting Point Agency.</p>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-8 border-l-4 border-brand-blue bg-white p-6 sm:p-7" data-testid="collaborators-note">
              <h3 className="font-display text-base font-extrabold tracking-tight text-navy">UN TEAM CHE SI ALLARGA QUANDO SERVE.</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Spesso fornisco anche collaboratori, ragazze e ragazzi immagine, attori e attrici e content creator: le persone giuste, davanti e dietro la camera.
              </p>
            </div>
          </Reveal>
        </div>
      </div>

      <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2">
        {MEANINGS.map((m, i) => (
          <Reveal key={m.n} delay={0.1 + i * 0.1}>
            <div data-testid={`chi-meaning-${m.n}`} className="relative h-full overflow-hidden border border-slate-200 bg-white p-7 sm:p-8">
              <span className="absolute inset-x-0 top-0 h-[3px] bg-brand-gradient" aria-hidden="true" />
              <span className="font-mono text-xs tracking-[0.25em] text-brand-blue">{m.n}</span>
              <h3 className="mt-3 font-display text-lg font-extrabold tracking-tight text-navy">{m.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{m.text}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <div className="mt-14 grid grid-cols-2 gap-px overflow-hidden border border-slate-200 bg-slate-200 lg:grid-cols-4" data-testid="chi-experience">
          {EXPERIENCE.map((e) => (
            <div key={e.label} className="group flex h-full flex-col justify-between bg-white p-6 transition-colors duration-300 hover:bg-brand-light">
              <span className="font-display text-xl font-black tracking-tight text-navy group-hover:text-brand-blue sm:text-2xl">{e.value}</span>
              <span className="mt-5 font-mono text-[10px] leading-relaxed tracking-[0.18em] text-slate-500">{e.label}</span>
            </div>
          ))}
        </div>
        <ul className="mt-6 flex flex-wrap gap-2">
          {SKILLS.map((s) => (
            <li key={s} className="rounded-full border border-navy/15 bg-white px-4 py-1.5 text-xs font-semibold text-navy">{s}</li>
          ))}
        </ul>
      </Reveal>

      <Reveal delay={0.15}>
        <div className="mt-16 flex flex-col items-start justify-between gap-8 border-t border-navy/10 pt-12 lg:flex-row lg:items-center">
          <p className="max-w-2xl font-display text-2xl font-black leading-snug tracking-tight text-navy sm:text-3xl">
            IL TUO PASSATO È IL TUO PERCORSO.<br />
            IL PROSSIMO PASSO È IL TUO <span className="text-brand-gradient">STARTING POINT.</span>
          </p>
          <ArrowCta testId="chi-cta" onClick={() => scrollToSection("contatti")}>
            TROVIAMO IL TUO PUNTO DI PARTENZA
          </ArrowCta>
        </div>
      </Reveal>
    </div>
  </section>
);
